import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, MapPin, Hammer, Timer, CheckCircle2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Logo, ThemeToggle, Footer, Eyebrow } from "@/components/sg/Brand";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-lg bg-card p-4">
      <Icon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
      <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function InquiryForm({ slug }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/showcase/${slug}/inquiry`, f); setSent(true); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (sent) return <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-xl p-6 text-sm" data-testid="inquiry-sent-msg">Thanks! Your request was sent. Expect a reply by email.</div>;
  return (
    <form onSubmit={submit} className="border border-border rounded-xl bg-card p-6 space-y-3" data-testid="inquiry-form">
      <div className="font-display font-bold text-lg">Request a quote</div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Name</Label><Input data-testid="inquiry-name" required minLength={2} value={f.name} onChange={set("name")} /></div>
        <div><Label>Phone (optional)</Label><Input data-testid="inquiry-phone" value={f.phone} onChange={set("phone")} /></div>
      </div>
      <div><Label>Email</Label><Input data-testid="inquiry-email" type="email" required value={f.email} onChange={set("email")} /></div>
      <div><Label>About your project</Label><Textarea data-testid="inquiry-message" required minLength={10} value={f.message} onChange={set("message")} className="min-h-[110px]" /></div>
      <Button data-testid="inquiry-submit-btn" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white">{busy ? "Sending…" : "Send request"}</Button>
    </form>
  );
}

export default function Showcase() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/showcase/${slug}`).then((r) => setData(r.data)).catch((e) => setError(formatErr(e.response?.data?.detail)));
  }, [slug]);
  const name = data?.company_name || data?.display_name || "Contractor";
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border"><div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between"><Logo /><ThemeToggle /></div></header>
      <main className="max-w-6xl mx-auto px-6 py-12">
        {error && <div className="text-muted-foreground" data-testid="showcase-error">{error}</div>}
        {!data && !error && <div className="text-muted-foreground">Loading…</div>}
        {data && (
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <Eyebrow className="inline-flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Verified on ScopeGuard</Eyebrow>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3" data-testid="showcase-name">{name}</h1>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                  {data.trade && <span className="inline-flex items-center gap-1"><Hammer className="h-4 w-4" />{data.trade}</span>}
                  {data.service_area && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{data.service_area}</span>}
                </div>
                {data.bio && <p className="mt-5 leading-relaxed max-w-2xl">{data.bio}</p>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stat icon={Hammer} label="Projects managed" value={data.stats.projects} />
                <Stat icon={CheckCircle2} label="Milestones signed off" value={data.stats.milestones_approved} />
                <Stat icon={Timer} label="Avg. approval time" value={data.stats.avg_approval_hours != null ? `${data.stats.avg_approval_hours}h` : "—"} />
              </div>
              <div>
                <div className="font-display text-xl font-bold mb-4">Featured projects</div>
                {data.projects.length === 0 && <div className="text-sm text-muted-foreground">No featured projects yet.</div>}
                <div className="space-y-4">
                  {data.projects.map((p) => (
                    <div key={p.id} className="border border-border rounded-xl bg-card p-5" data-testid={`showcase-project-${p.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-display font-bold">{p.title}</div>
                        <span className="text-xs text-muted-foreground">{p.milestones_approved}/{p.milestones_total} milestones signed{p.completed ? " · Completed" : ""}</span>
                      </div>
                      <ul className="mt-3 space-y-1.5">
                        {p.approvals.map((a) => (
                          <li key={a.name + a.approved_at} className="text-sm flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />{a.name}
                            <span className="text-muted-foreground text-xs ml-auto">{fmtDate(a.approved_at)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 lg:sticky lg:top-8 self-start"><InquiryForm slug={slug} /></div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
